// MUNYA OS · Router cliente mínimo · sin recargas
// Reemplaza window.location.pathname + <a href> por usePathname() + <Link>
// History API pushState/popstate + custom event para re-render coordinado.

import { useEffect, useState, type AnchorHTMLAttributes, type ReactElement } from 'react';

const NAV_EVENT = 'munya-nav';

/**
 * Hook: devuelve el pathname actual y re-renderiza en cada cambio.
 */
export function usePathname(): string {
  const [path, setPath] = useState(() =>
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );

  useEffect(() => {
    const sync = (): void => setPath(window.location.pathname);
    window.addEventListener('popstate', sync);
    window.addEventListener(NAV_EVENT, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(NAV_EVENT, sync);
    };
  }, []);

  return path;
}

/**
 * Hook: lee y reacciona a cambios del query string.
 * Reemplaza el patrón `new URLSearchParams(window.location.search).get('lead')`
 * que sólo se evalúa al montar.
 */
export function useSearchParam(name: string): string | null {
  const [value, setValue] = useState(() =>
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get(name),
  );
  useEffect(() => {
    const sync = (): void => setValue(new URLSearchParams(window.location.search).get(name));
    window.addEventListener('popstate', sync);
    window.addEventListener(NAV_EVENT, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(NAV_EVENT, sync);
    };
  }, [name]);
  return value;
}

/**
 * Navega programáticamente sin recargar.
 */
export function navigate(href: string, opts: { replace?: boolean } = {}): void {
  if (opts.replace) window.history.replaceState({}, '', href);
  else window.history.pushState({}, '', href);
  window.dispatchEvent(new Event(NAV_EVENT));
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  /** Cuando true, hace un full navigation (para /auth/*, externals, etc.) */
  external?: boolean;
};

/**
 * <Link> intercepta click y hace pushState · igual API que <a>.
 * Si href empieza por http(s):// o por /auth/ → comportamiento <a> normal.
 */
export function Link({ href, external, onClick, children, ...rest }: LinkProps): ReactElement {
  const isExternal =
    external ||
    /^https?:\/\//.test(href) ||
    href.startsWith('/auth/') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:');

  return (
    <a
      {...rest}
      href={href}
      onClick={(e) => {
        if (onClick) onClick(e);
        if (e.defaultPrevented) return;
        if (isExternal) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // open in new tab
        e.preventDefault();
        navigate(href);
        // Scroll arriba como hace el browser por defecto
        window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      }}
    >
      {children}
    </a>
  );
}
